import React, { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import FilterDropdown from '../../common/FilterDropdown/FilterDropdown';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouterIcon from '@mui/icons-material/Router';
import BusinessIcon from '@mui/icons-material/Business';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import DevicesIcon from '@mui/icons-material/Devices';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useAutocompleteDropdownHeight } from '../../Hooks/useAutocompleteDropdownHeight';
import { useDebounce } from '../../Hooks/useDebounce';
import { useAPI } from '../../Hooks/useAPIs';
import { DEVICE_TYPE_OPTIONS } from '../../utils/deviceTypes';

const DEVICE_FILTERS_ENDPOINT = 'api/v1/devices/filters';
const USER_PAGE_SIZE = 100;

const BOOLEAN_FILTER_VALUE = {
  TRUE: 'true',
  FALSE: 'false',
};

const ONLINE_FILTER_OPTIONS = [
  { value: BOOLEAN_FILTER_VALUE.TRUE, label: 'Online' },
  { value: BOOLEAN_FILTER_VALUE.FALSE, label: 'Offline' },
];

const RUNNING_FILTER_OPTIONS = [
  { value: BOOLEAN_FILTER_VALUE.TRUE, label: 'Running' },
  { value: BOOLEAN_FILTER_VALUE.FALSE, label: 'Stopped' },
];

const LOGGING_FILTER_OPTIONS = [
  { value: BOOLEAN_FILTER_VALUE.TRUE, label: 'Logging' },
  { value: BOOLEAN_FILTER_VALUE.FALSE, label: 'Not Logging' },
];

const STATUS_FILTER_CONFIG = [
  { field: 'online', label: 'Online', icon: PowerSettingsNewIcon, options: ONLINE_FILTER_OPTIONS },
  { field: 'running', label: 'Running', icon: PlayArrowIcon, options: RUNNING_FILTER_OPTIONS },
  { field: 'logging', label: 'Logging', icon: ReceiptLongIcon, options: LOGGING_FILTER_OPTIONS },
];

const normalizeUserOption = (option) => {
  if (typeof option === 'string') {
    return {
      email: option,
      displayName: '',
    };
  }

  if (!option || typeof option !== 'object') {
    return null;
  }

  const email = option.email || option.value || option.label || '';
  if (!email) {
    return null;
  }

  return {
    email,
    displayName: option.display_name || option.displayName || option.name || '',
  };
};

const getUserOptionId = (option) => option?.email || '';
const getUserOptionLabel = (option) => option?.displayName || option?.email || '';
const mapStaticFilterValue = (selectedValues, options) => (selectedValues || []).map((selectedValue) => {
  const matchedOption = options.find((option) => option.value === selectedValue);
  return matchedOption || { value: selectedValue, label: selectedValue };
});
const getStaticOptionValue = (option) => (typeof option === 'object' && option !== null ? option.value : option);
const getRegionCode = (option) => (typeof option === 'object' && option?.code ? option.code : option);
const GENERAL_REMOTE_FILTER_CONFIG = [
  { field: 'vin', label: 'VIN', icon: DirectionsCarIcon },
  { field: 'mac_addr', label: 'MAC Address', icon: RouterIcon },
];

export const DeviceFilters = ({ filters, debouncedFilters, handleFilterChange, createIsoStringWithTimezone, mapping, REGIONS }) => {
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isUserAutocompleteOpen, setIsUserAutocompleteOpen] = useState(false);
  const debouncedUserSearchTerm = useDebounce(userSearchTerm, 300);
  const {
    containerRef: userAutocompleteRef,
    availableHeight: userDropdownHeight,
    updateAvailableHeight: updateUserDropdownHeight,
  } = useAutocompleteDropdownHeight({ ancestorSelector: null });
  const { apiCall, isMsalReady } = useAPI();
  const sharedDateFilters = useMemo(() => ({
    start_datetime: debouncedFilters.start_datetime,
    end_datetime: debouncedFilters.end_datetime,
    createIsoStringWithTimezone,
  }), [debouncedFilters.end_datetime, debouncedFilters.start_datetime, createIsoStringWithTimezone]);
  const regionLookup = useMemo(
    () => new Map((REGIONS || []).map((region) => [region.code, region])),
    [REGIONS]
  );

  const getOtherFilters = useCallback((currentField) => {
    const other = {};
    Object.keys(filters).forEach((key) => {
      if (key !== currentField && Array.isArray(filters[key]) && filters[key].length > 0) {
        other[key] = filters[key];
      }
    });
    return other;
  }, [filters]);

  const userOtherFilters = useMemo(() => getOtherFilters('user'), [getOtherFilters]);

  const userFilterQuery = useInfiniteQuery({
    queryKey: ['device-user-filter-options', debouncedUserSearchTerm, JSON.stringify(userOtherFilters), debouncedFilters.start_datetime, debouncedFilters.end_datetime],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        field: 'user',
        limit: String(USER_PAGE_SIZE),
        offset: String(pageParam),
      });

      if (debouncedUserSearchTerm?.trim()) {
        params.append('q', debouncedUserSearchTerm.trim());
      }

      if (debouncedFilters.start_datetime && createIsoStringWithTimezone) {
        const iso = createIsoStringWithTimezone(debouncedFilters.start_datetime);
        if (iso) params.append('start_date', iso);
      }
      if (debouncedFilters.end_datetime && createIsoStringWithTimezone) {
        const iso = createIsoStringWithTimezone(debouncedFilters.end_datetime);
        if (iso) params.append('end_date', iso);
      }

      Object.entries(userOtherFilters).forEach(([key, val]) => {
        if (!Array.isArray(val) || val.length === 0) return;
        const param = mapping[key] || key;
        params.append(param, val.join(','));
      });

      const response = await apiCall('GET', `api/v1/devices/filters?${params.toString()}`);
      const data = response?.data;

      const rawOptions = data?.data?.user || data?.user || data?.results || [];
      const normalized = (Array.isArray(rawOptions) ? rawOptions : [])
        .map(normalizeUserOption)
        .filter(Boolean);

      const hasMore = typeof data?.has_more === 'boolean'
        ? data.has_more
        : typeof data?.hasMore === 'boolean'
          ? data.hasMore
          : normalized.length >= USER_PAGE_SIZE;

      return {
        results: normalized,
        hasMore,
        nextOffset: pageParam + USER_PAGE_SIZE,
      };
    },
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextOffset : undefined),
    initialPageParam: 0,
    enabled: isUserAutocompleteOpen && isMsalReady,
    placeholderData: keepPreviousData,
  });

  const userOptions = useMemo(() => {
    const pages = userFilterQuery.data?.pages || [];
    const merged = pages.flatMap((page) => page.results || []);
    const seen = new Set();

    return merged.filter((user) => {
      const userId = getUserOptionId(user);
      if (!userId || seen.has(userId)) {
        return false;
      }
      seen.add(userId);
      return true;
    });
  }, [userFilterQuery.data]);

  const userOptionLookup = useMemo(
    () => new Map(userOptions.map((user) => [getUserOptionId(user), user])),
    [userOptions]
  );

  const selectedUserEmails = useMemo(() => Array.isArray(filters.user) ? filters.user : [], [filters.user]);

  const selectedUsers = useMemo(
    () => selectedUserEmails.map((email) => userOptionLookup.get(email) || { email, displayName: '' }),
    [selectedUserEmails, userOptionLookup]
  );

  const handleUserSelectionChange = useCallback((event, nextUsers) => {
    handleFilterChange('user', nextUsers.map((user) => user.email));
  }, [handleFilterChange]);

  const handleUserListboxScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 40) {
      if (userFilterQuery.hasNextPage && !userFilterQuery.isFetchingNextPage) {
        userFilterQuery.fetchNextPage();
      }
    }
  }, [userFilterQuery]);

  const getRemoteFilterProps = useCallback((field) => ({
    customEndpoint: DEVICE_FILTERS_ENDPOINT,
    multiple: true,
    dateFilters: sharedDateFilters,
    otherFilters: getOtherFilters(field),
    fieldMapping: mapping,
  }), [getOtherFilters, mapping, sharedDateFilters]);

  const handleSimpleArrayFilterChange = useCallback((field, value) => {
    handleFilterChange(field, value);
  }, [handleFilterChange]);

  const handleStaticFilterChange = useCallback((field, value) => {
    handleFilterChange(field, value.map(getStaticOptionValue));
  }, [handleFilterChange]);

  const handleRegionFilterChange = useCallback((value) => {
    handleFilterChange('region', value.map(getRegionCode));
  }, [handleFilterChange]);

  const getRegionOptionLabel = useCallback((option) => {
    const code = getRegionCode(option);
    const region = regionLookup.get(code);
    return region ? `${region.code} - ${region.name}` : code;
  }, [regionLookup]);

  const isRegionOptionEqualToValue = useCallback((option, value) => getRegionCode(option) === getRegionCode(value), []);

  const userFilterControl = (
    <Box className="device-user-picker" ref={userAutocompleteRef}>
      <Box className="device-user-picker-header">
        <PersonIcon className="device-user-picker-icon" />
        <Typography variant="subtitle2" className="device-user-picker-title">User</Typography>
      </Box>

      <Autocomplete
        multiple
        open={isUserAutocompleteOpen}
        onOpen={() => {
          setIsUserAutocompleteOpen(true);
          updateUserDropdownHeight();
        }}
        onClose={() => setIsUserAutocompleteOpen(false)}
        fullWidth
        size="small"
        disableCloseOnSelect
        blurOnSelect={false}
        clearOnBlur={false}
        options={userOptions}
        value={selectedUsers}
        inputValue={userSearchTerm}
        loading={userFilterQuery.isFetching || userFilterQuery.isFetchingNextPage}
        filterOptions={(options) => options}
        getOptionLabel={getUserOptionLabel}
        isOptionEqualToValue={(option, value) => getUserOptionId(option) === getUserOptionId(value)}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'reset' || reason === 'selectOption') return;
          setUserSearchTerm(newInputValue);
          setIsUserAutocompleteOpen(true);
        }}
        onChange={handleUserSelectionChange}
        ListboxProps={{
          onScroll: handleUserListboxScroll,
          style: { maxHeight: userDropdownHeight },
          className: 'device-user-search-listbox',
        }}
        slotProps={{
          paper: {
            className: 'device-user-search-paper',
          },
          popper: {
            className: 'device-user-search-popper',
            placement: 'bottom',
            modifiers: [
              { name: 'flip', enabled: false },
            ],
          },
        }}
        renderTags={(tagValue, getTagProps) => tagValue.map((user, index) => (
          <Chip
            {...getTagProps({ index })}
            key={user.email}
            size="small"
            label={user.displayName || user.email}
            className="device-user-search-tag"
          />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            className="device-user-search-input"
            placeholder="Search user..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {(userFilterQuery.isFetching || userFilterQuery.isFetchingNextPage) ? (
                    <CircularProgress color="inherit" size={20} style={{ marginRight: 8 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const isSelected = selectedUserEmails.includes(option.email);

          return (
            <li
              {...props}
              key={option.email}
              className={`device-user-search-option-li ${props.className || ''}`}
            >
              <Box className={`device-user-search-option-content ${isSelected ? 'selected' : ''}`}>
                <Box className="device-user-search-option-row">
                  <PersonIcon className="device-user-search-option-icon" />
                  <Typography variant="body2" className="device-user-search-option-text">
                    {option.displayName || option.email}
                  </Typography>
                  <Box className="device-user-search-option-badges">
                    {isSelected && (
                      <span className="device-user-search-option-badge device-user-search-option-badge-selected">
                        Added
                      </span>
                    )}
                  </Box>
                  {isSelected && <CheckCircleIcon className="device-user-search-option-check" />}
                </Box>
                {option.displayName && (
                  <Typography variant="caption" color="text.secondary" className="device-user-search-option-meta">
                    {option.email}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        noOptionsText={userFilterQuery.isFetching ? 'Loading...' : 'No users found'}
      />
    </Box>
  );

  const fire3InventoryFilters = (
    <Box className="device-fire3-filter-board">
      <Box className="device-fire3-filter-board-header">
        <Inventory2OutlinedIcon className="device-fire3-filter-board-icon" />
        <Box>
          <Typography variant="subtitle2" className="device-fire3-filter-board-title">
            Fire 3 Inventory Filters
          </Typography>
          <Typography variant="caption" color="text.secondary" className="device-fire3-filter-board-caption">
            Fleet, Online, Running, and Logging only apply to Fire 3 inventory data.
          </Typography>
        </Box>
      </Box>

      <Box className="device-fire3-filter-board-content">
        <FilterDropdown
          field="remote_fleet_name"
          label="Fleet"
          value={filters.remote_fleet_name || []}
          onChange={(value) => handleSimpleArrayFilterChange('remote_fleet_name', value)}
          icon={BusinessIcon}
          {...getRemoteFilterProps('remote_fleet_name')}
        />
        {STATUS_FILTER_CONFIG.map((statusFilter) => (
          <FilterDropdown
            key={statusFilter.field}
            field={statusFilter.field}
            label={statusFilter.label}
            value={mapStaticFilterValue(filters[statusFilter.field], statusFilter.options)}
            onChange={(value) => handleStaticFilterChange(statusFilter.field, value)}
            multiple={true}
            icon={statusFilter.icon}
            staticOptions={statusFilter.options}
            disableSearch={true}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box className="device-filters-root">
      {userFilterControl}

      {GENERAL_REMOTE_FILTER_CONFIG.map((filterConfig) => (
        <FilterDropdown
          key={filterConfig.field}
          field={filterConfig.field}
          label={filterConfig.label}
          value={filters[filterConfig.field] || []}
          onChange={(value) => handleSimpleArrayFilterChange(filterConfig.field, value)}
          icon={filterConfig.icon}
          {...getRemoteFilterProps(filterConfig.field)}
        />
      ))}
      <FilterDropdown
        field="region"
        label="Region"
        value={filters.region || []}
        onChange={handleRegionFilterChange}
        icon={PublicIcon}
        getOptionLabel={getRegionOptionLabel}
        isOptionEqualToValue={isRegionOptionEqualToValue}
        {...getRemoteFilterProps('region')}
      />
      <FilterDropdown
        field="device_type"
        label="Device Type"
        value={(filters.device_type || []).map((deviceType) => ({ value: deviceType, label: deviceType }))}
        onChange={(value) => handleStaticFilterChange('device_type', value)}
        multiple={true}
        icon={DevicesIcon}
        staticOptions={DEVICE_TYPE_OPTIONS}
        disableSearch={true}
      />
      {fire3InventoryFilters}
    </Box>
  );
};

